import { createPortal } from "react-dom";
import { useTheme } from "@material-ui/styles";
import {
  Theme,
  Select,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@material-ui/core";
import { useAppData } from "../../../common/hooks/useAppData";
import { Identifier } from "react-admin";
import { Business } from "@material-ui/icons";
import { useCallback, useContext } from "react";
import { isArray } from "lodash";
import { AppBarFiltersContainerContext } from "../app-bar-filters/AppBarFiltersContainerContext";

interface AppBarCompanyFilterProps {
  selectedCompanies?: Identifier[];
  onSelectedCompaniesChange?: (ids: string[]) => void;
}

interface EmptyProps {
  header: boolean;
  theme: Theme;
}

const renderEmptyCompanyAppbarFilter = ({ header, theme }: EmptyProps) => {
  return (
    <MenuItem className="appbar-filter-list-item" value="">
      <ListItemIcon
        style={{ color: header ? theme.palette.common.white : "auto" }}>
        <Business />
      </ListItemIcon>
      <ListItemText>
        <Typography variant="body2">All companies</Typography>
      </ListItemText>
    </MenuItem>
  );
};

export const AppbarCompaniesFilter = ({
  selectedCompanies,
  onSelectedCompaniesChange,
}: AppBarCompanyFilterProps) => {
  const theme: Theme = useTheme();
  const companies = useAppData((x) => x.companies);
  const { container: appbarFiltersContainer } = useContext(
    AppBarFiltersContainerContext
  );

  const renderCompanyAppbarFilterValue = useCallback(
    (id: Identifier, header: boolean) => (
      <MenuItem key={id} className="appbar-filter-list-item" value={id}>
        <ListItemIcon
          style={{ color: header ? theme.palette.common.white : "auto" }}>
          <Business />
        </ListItemIcon>
        <ListItemText>
          <Typography variant="body2">
            {companies.find((x) => x.id === id)?.name}
          </Typography>
        </ListItemText>
      </MenuItem>
    ),
    [theme, companies]
  );

  const renderSelectedCompanyValues = useCallback(
    (values: Array<string | null | undefined>) => (
      <MenuItem className="appbar-filter-list-item">
        <ListItemIcon style={{ color: theme.palette.common.white }}>
          <Business />
        </ListItemIcon>
        <ListItemText>
          <Typography variant="body2">{values.join(", ")}</Typography>
        </ListItemText>
      </MenuItem>
    ),
    [theme]
  );

  if (!companies.length) {
    return null;
  }

  return appbarFiltersContainer
    ? createPortal(
        <>
          <Select
            multiple
            autoFocus={false}
            className="appbar-filter companies-filter"
            disableUnderline
            label=""
            value={selectedCompanies}
            displayEmpty
            renderValue={(value) => {
              if (!isArray(value) || !value.length) {
                return renderEmptyCompanyAppbarFilter({ theme, header: true });
              }

              const names = value.map(
                (v) => companies.find((x) => x.id === v)?.name
              );
              return names
                ? renderSelectedCompanyValues(names)
                : renderEmptyCompanyAppbarFilter({ theme, header: true });
            }}
            onChange={(e) => {
              if (!onSelectedCompaniesChange) {
                return;
              }

              const target = e.target as HTMLSelectElement;
              const value = target.value as string | string[];
              if (!Array.isArray(value)) {
                console.error("The select input value was not array");
                return;
              }

              if (value.some((x) => !x)) {
                onSelectedCompaniesChange([]);
                return;
              }

              const values: string[] = [];
              if (!value?.length) {
                onSelectedCompaniesChange(values);
                return;
              }

              for (let i = 0, l = value.length; i < l; i += 1) {
                if (value[i]) {
                  values.push(value[i]);
                }
              }
              onSelectedCompaniesChange(values);
            }}>
            {[
              <MenuItem
                key={0}
                className="appbar-filter-list-item"
                value={undefined}>
                <ListItemIcon style={{ color: "auto" }}>
                  <Business />
                </ListItemIcon>
                <ListItemText>
                  <Typography variant="body2">All companies</Typography>
                </ListItemText>
              </MenuItem>,
              ...companies.map((x) =>
                renderCompanyAppbarFilterValue(x.id ?? "", false)
              ),
            ]}
          </Select>
        </>,
        appbarFiltersContainer
      )
    : null;
};
